import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useAppSelector, useAppDispatch } from './redux';
import { playerService, Player } from '@/services/playerService';
import { leagueService } from '@/services/leagueService';
import { scheduleService } from '@/services/scheduleService';
import { conflictService } from '@/services/conflictService';
import { organizationService } from '@/services/organizationService';
import { setOrganizationSettings } from '@/store/organizationSlice';
import { prefetchImages } from '@/utils/imagePrefetch';

/**
 * Hook to prefetch data for all main pages in the background
 * This ensures instant loading when users navigate between pages
 */
export const usePrefetchData = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (!user?.currentOrganizationId) return;

    const orgId = user.currentOrganizationId;

    // Fetch organization settings
    organizationService.getOrganizationSettings(orgId)
      .then(settings => {
        dispatch(setOrganizationSettings(settings));
      })
      .catch(error => {
        console.error('Failed to fetch organization settings:', error);
      });

    // Prefetch players data and their logos
    queryClient.prefetchQuery(
      ['players', orgId],
      async () => {
        const players = await playerService.getPlayers();

        // Collect logo URLs from player league assignments
        const logoUrls: (string | null | undefined)[] = [];
        players.forEach((player: Player) => {
          player.leagueAssignments?.forEach(assignment => {
            // Team logos
            if (assignment.teamLogoPath) {
              logoUrls.push(`/${assignment.teamLogoPath}`);
            }
            // League logos
            if (assignment.league?.logoPath) {
              logoUrls.push(`/${assignment.league.logoPath}`);
            } else if (assignment.league?.logoUrl) {
              logoUrls.push(assignment.league.logoUrl);
            }
            // Club logos
            if (assignment.club?.logoUrl) {
              logoUrls.push(assignment.club.logoUrl);
            }
          });
        });

        // Prefetch all logos
        if (logoUrls.length > 0) {
          prefetchImages(logoUrls);
        }

        return players;
      },
      {
        staleTime: 2 * 60 * 1000, // Consider fresh for 2 minutes
      }
    );

    // Prefetch leagues data and their logos
    queryClient.prefetchQuery(
      ['leagues', orgId],
      async () => {
        const response = await leagueService.getLeagues(1, 100);
        const leaguesData = (response.data || []);

        // Collect league logo URLs
        const logoUrls: (string | null | undefined)[] = [];
        leaguesData.forEach((league: any) => {
          if (league.logoPath) {
            logoUrls.push(`/${league.logoPath}`);
          } else if (league.logoUrl) {
            logoUrls.push(league.logoUrl);
          }
        });

        // Prefetch all league logos
        if (logoUrls.length > 0) {
          prefetchImages(logoUrls);
        }

        return leaguesData.map((league: any) => ({
          ...league,
          syncStats: {
            totalMatches: league._count?.matches || 0,
            newMatches: 0,
            updatedMatches: 0,
            uniqueTeams: 0,
            syncDuration: 0,
            status: 'NEVER_SYNCED' as const
          },
        }));
      },
      {
        staleTime: 2 * 60 * 1000,
      }
    );

    // Prefetch upcoming matches
    queryClient.prefetchQuery(
      ['upcoming-matches', orgId],
      () => scheduleService.getUpcomingMatches(365),
      {
        staleTime: 5 * 60 * 1000, // Matches change less frequently
      }
    );

    // Prefetch conflict summary
    queryClient.prefetchQuery(
      ['conflict-summary', orgId],
      conflictService.getConflictSummary,
      {
        staleTime: 5 * 60 * 1000,
      }
    );

    // Prefetch conflicts
    queryClient.prefetchQuery(
      ['conflicts', orgId],
      () => conflictService.getConflicts(),
      {
        staleTime: 5 * 60 * 1000,
      }
    );

  }, [user?.currentOrganizationId, queryClient]);
};
